import React, { createContext, useContext, useState, useEffect } from "react";

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    mobileNumber: "",
    demo: "Banking",
  });
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("bankingAppSettings");
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
    setIsLoaded(true);
  }, []);

  // Save settings to localStorage whenever they change (but only after initial load)
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem("bankingAppSettings", JSON.stringify(settings));
    }
  }, [settings, isLoaded]);

  const updateSettings = (newSettings) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  };

  const demoOptions = [
    {
      value: "Banking",
      label: "Banking",
      tabs: [
        "Home",
        "My Beneficiaries",
        "My Cards",
        "Business Controller",
        "Voice",
      ],
      token:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJJRCI6IjE4MjkwIiwiTm9tYnJlIjoiQWRhbSIsIkFwZWxsaWRvIjoiRG9lIiwiQ29tcGFueSI6Ijk1IiwiQWxpYXMiOiJkZXYtbWFsbCIsIkV4dGVybmFsSWQiOiIiLCJEZXBlbmRhbnQiOiJGYWxzZSIsIkFjY2VzcyI6IntcImlkXCI6NjE5LFwiYWRtaW5cIjoxMDAsXCJraW5kXCI6LTEsXCJjYXRlZ29yaWVzXCI6W10sXCJSXCI6Wzc5LDgwLDgxLDIzLDUyLDg0LDg4LDg1LDQ0XSxcIldcIjpbNzksODAsODEsMjMsNTIsODQsODgsODUsNDRdfSIsIm5iZiI6MTc3MDkwNTM0MSwiZXhwIjoxNzg2NTQzNzQxLCJpYXQiOjE3NzA5MDUzNDEsImlzcyI6Imh0dHBzOi8vYXBwLW9uLmNsb3VkIiwiYXVkIjoiaHR0cHM6Ly9hcHAtb24uY2xvdWQifQ.Ympq8MY_tkUgNeXEpp-tYkUzqn1OzbWMLh3DwexjQAs",
    },
    {
      value: "Courrier",
      label: "Courrier",
      tabs: ["Business Controller"],
      token:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJJRCI6IjE4MjkwIiwiTm9tYnJlIjoiQWRhbSIsIkFwZWxsaWRvIjoiRG9lIiwiQ29tcGFueSI6IjkwIiwiQWxpYXMiOiJkZXYtZGVsaXZlcnkiLCJFeHRlcm5hbElkIjoiIiwiRGVwZW5kYW50IjoiRmFsc2UiLCJBY2Nlc3MiOiJ7XCJpZFwiOjU5NCxcImFkbWluXCI6MTAwLFwia2luZFwiOi0xLFwiY2F0ZWdvcmllc1wiOltdLFwiUlwiOls3OSw4MCwyMyw1Miw4NSw0NCw4NCw4MV0sXCJXXCI6Wzc5LDgwLDIzLDUyLDg1LDQ0LDg0LDgxXX0iLCJuYmYiOjE3NTg0NDMzNzEsImV4cCI6MTg5MzQ1NjAwMCwiaWF0IjoxNzU4NDQzMzcxLCJpc3MiOiJodHRwczovL2FwcC1vbi5jbG91ZCIsImF1ZCI6Imh0dHBzOi8vYXBwLW9uLmNsb3VkIn0.QXFStSR7dPSjb3W_5xf_AcGa_7BhHSNJum_HQ-I_K_A",
    },
    {
      value: "Facilities",
      label: "Facilities",
      tabs: ["Business Controller", "CRUD", "Voice"],
      token:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJJRCI6IjE4MjkwIiwiTm9tYnJlIjoiQWRhbSIsIkFwZWxsaWRvIjoiRG9lIiwiQ29tcGFueSI6Ijg4IiwiQWxpYXMiOiJkZXYtZmFjaWxpdGllcyIsIkV4dGVybmFsSWQiOiIiLCJEZXBlbmRhbnQiOiJGYWxzZSIsIkFjY2VzcyI6IntcImlkXCI6NTkyLFwiYWRtaW5cIjoxMDAsXCJraW5kXCI6LTEsXCJjYXRlZ29yaWVzXCI6WzBdLFwiUlwiOls3OSw4MCwyMyw1Miw4NCw0NCw4MV0sXCJXXCI6Wzc5LDgwLDIzLDUyLDg0LDQ0LDgxXX0iLCJuYmYiOjE3NTg4MTYwNDgsImV4cCI6MTg5MzQ5OTc5OSwiaWF0IjoxNzU4ODE2MDQ4LCJpc3MiOiJodHRwczovL2FwcC1vbi5jbG91ZCIsImF1ZCI6Imh0dHBzOi8vYXBwLW9uLmNsb3VkIn0.ARvBUSDYCeBRwxqIqPDrrFdc0QBkExYNy_xXe_AJt2Q",
    },
    {
      value: "Healthcare",
      label: "Healthcare",
      tabs: ["Business Controller", "Voice"],
      token:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJJRCI6IjE4MjkwIiwiTm9tYnJlIjoiQWRhbSIsIkFwZWxsaWRvIjoiRG9lIiwiQ29tcGFueSI6Ijg3IiwiQWxpYXMiOiJkZXYtaGVhbHRoY2FyZSIsIkV4dGVybmFsSWQiOiIiLCJEZXBlbmRhbnQiOiJGYWxzZSIsIkFjY2VzcyI6IntcImlkXCI6NTg5LFwiYWRtaW5cIjoxMDAsXCJraW5kXCI6LTEsXCJjYXRlZ29yaWVzXCI6WzBdLFwiUlwiOls3OSw4MCwyMyw1Miw4NCw0NCw4MSw4Ml0sXCJXXCI6Wzc5LDgwLDIzLDUyLDg0LDQ0LDgyLDgxXX0iLCJuYmYiOjE3NjI3OTI1NzcsImV4cCI6MTg5MzQ1NjAwMCwiaWF0IjoxNzYyNzkyNTc3LCJpc3MiOiJodHRwczovL2FwcC1vbi5jbG91ZCIsImF1ZCI6Imh0dHBzOi8vYXBwLW9uLmNsb3VkIn0.lZq6wH08hKLHzvz2r_sNrvqWbj8ZHGkEvxS7-dpChZ4",
    },
  ];

  const getCurrentTabs = () => {
    const currentDemo = demoOptions.find(
      (demo) => demo.value === settings.demo,
    );
    return currentDemo ? currentDemo.tabs : demoOptions[0].tabs;
  };

  const getFirstTabPath = (demoValue = settings.demo) => {
    const demo = demoOptions.find((d) => d.value === demoValue);
    if (!demo || !demo.tabs.length) return "/";

    const firstTab = demo.tabs[0];
    const pathMapping = {
      Home: "/",
      "My Beneficiaries": "/beneficiaries",
      "My Cards": "/cards",
      "Business Controller": "/business-controller",
      CRUD: "/crud",
    };

    return pathMapping[firstTab] || "/";
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings,
        demoOptions,
        getCurrentTabs,
        getFirstTabPath,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
